import React, { useLayoutEffect, useRef } from "react";
import { Check } from "lucide-react";
import { playDustTransfer } from "../utils/dustTransfer";

/**
 * The one selection marker for choice cards. `radio` marks the single pick in
 * a group; `check` marks one of several picks. The fill is always mounted so
 * deselecting can animate out as well as in.
 */
export default function SelectionDot({
  selected,
  group,
  value,
  variant = "radio",
}: {
  selected: boolean;
  group?: string;
  value?: string;
  variant?: "radio" | "check";
}) {
  return (
    <span
      className={`selection-dot is-${variant} ${selected ? "is-on" : ""}`}
      data-dot-group={group}
      data-dot-value={value}
      aria-hidden="true"
    >
      <span className="selection-dot-fill" data-dot-fill>
        {variant === "check" && <Check size={11} strokeWidth={3} />}
      </span>
    </span>
  );
}

/**
 * When the selected value of a radio group changes, carry the dot from the
 * old pick to the new one. Runs before paint so the new dot never flashes full.
 */
export function useDustTransfer(group: string, value: string) {
  const previous = useRef(value);
  useLayoutEffect(() => {
    const from = previous.current;
    previous.current = value;
    if (from === value) return;
    const dot = (v: string) => document.querySelector(`[data-dot-group="${group}"][data-dot-value="${v}"]`);
    const fromDot = dot(from);
    const toDot = dot(value);
    if (fromDot && toDot?.classList.contains("is-on")) playDustTransfer(fromDot, toDot);
  }, [group, value]);
}
